import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { ProgressBar } from "@/components/common/Progress";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActionLinkPicker, LinkChip } from "@/components/workspace/ActionLinkPicker";
import { EvidencePanel, HistoryTimeline } from "@/components/workspace/WorkItemParts";
import { fmtDate, ACTION_STATUS, LEVEL, RAG, BUSINESS_AREAS, OVERDUE_REASONS, relDays } from "@/lib/format";
import { toast } from "sonner";

const STATUSES = ["not_started", "in_progress", "waiting", "blocked", "completed", "cancelled"];
const LEVELS = ["low", "normal", "high", "critical"];
const BOARD_COLS = [
  { key: "not_started", label: "Not started" },
  { key: "in_progress", label: "In progress" },
  { key: "blocked", label: "Blocked / Waiting", match: ["blocked", "waiting"] },
  { key: "completed", label: "Completed" },
];

export default function Actions() {
  const { orgId } = useOrg();
  const { canEdit, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [actions, setActions] = useState(null);
  const [members, setMembers] = useState([]);
  const [view, setView] = useState("list");
  const [scope, setScope] = useState(searchParams.get("scope") || "all");
  const [areaFilter, setAreaFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [dueFilter, setDueFilter] = useState(searchParams.get("due") || "all");
  const [month, setMonth] = useState(dayjs().startOf("month"));
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get(scope === "needs_link"
    ? `/actions?org_id=${orgId}&needs_link_review=true`
    : `/actions?org_id=${orgId}&scope=${scope}`).then((r) => setActions(r.data));
  useEffect(() => {
    if (!orgId) return;
    setActions(null);
    load();
    api.get(`/organisations/${orgId}/members`).then((r) => setMembers(r.data));
  }, [orgId, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!actions) return [];
    const now = dayjs();
    const dow = now.day();
    const monday = now.subtract((dow + 6) % 7, "day").startOf("day");
    const sunday = monday.add(6, "day").endOf("day");
    return actions.filter((a) => {
      if (areaFilter !== "all" && a.business_area !== areaFilter) return false;
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (dueFilter !== "all") {
        const open = !["completed", "cancelled"].includes(a.status);
        if (!a.due_date || !open) return false;
        const d = dayjs(a.due_date);
        if (dueFilter === "today" && !d.isSame(now, "day")) return false;
        if (dueFilter === "week" && (d.isBefore(monday) || d.isAfter(sunday))) return false;
      }
      return true;
    });
  }, [actions, areaFilter, statusFilter, dueFilter]);

  const calActions = useMemo(() => {
    if (!actions) return [];
    return actions.filter((a) =>
      (areaFilter === "all" || a.business_area === areaFilter) &&
      (statusFilter === "all" || a.status === statusFilter)
    );
  }, [actions, areaFilter, statusFilter]);

  if (!actions) return <PageLoading />;
  const unlinked = scope === "needs_link" ? 0 : actions.filter((a) => a.needs_link_review && !["completed", "cancelled"].includes(a.status)).length;

  return (
    <div data-testid="actions-page">
      <PageHeader
        eyebrow="Accountability"
        title="Actions"
        subtitle="Every commitment, owner and deadline in one place — so nothing slips."
        icon="ListChecks"
        actions={canEdit && (
          <Button onClick={() => setCreating(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="new-action-btn">
            <Icons.Plus size={16} className="mr-1.5" /> New action
          </Button>
        )}
      />

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="h-9 w-[160px] bg-white" data-testid="scope-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="mine">My actions</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="needs_link">Needs linking</SelectItem>
            </SelectContent>
          </Select>
          <Select value={areaFilter} onValueChange={setAreaFilter}>
            <SelectTrigger className="h-9 w-[190px] bg-white" data-testid="area-filter"><SelectValue placeholder="Area" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All business areas</SelectItem>
              {BUSINESS_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{ACTION_STATUS[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={dueFilter} onValueChange={setDueFilter}>
            <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="due-filter"><SelectValue placeholder="Due" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any due date</SelectItem>
              <SelectItem value="today">Due today</SelectItem>
              <SelectItem value="week">Due this week</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="bg-[#E4E8EF]">
            <TabsTrigger value="list" data-testid="view-list"><Icons.List size={15} className="mr-1" /> List</TabsTrigger>
            <TabsTrigger value="board" data-testid="view-board"><Icons.Columns3 size={15} className="mr-1" /> Board</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="view-calendar"><Icons.Calendar size={15} className="mr-1" /> Calendar</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {unlinked > 0 && (
        <div className="mb-5 flex flex-col gap-3 rounded-xl border border-[#FFC24D]/60 bg-[#FFB000]/[0.06] px-4 py-3 sm:flex-row sm:items-center" data-testid="needs-link-banner">
          <Icons.Link2 size={18} className="shrink-0 text-[#B87F1E]" />
          <p className="flex-1 text-sm text-[#1E2D44]/80">
            <b className="text-[#0B1729]">{unlinked} action{unlinked === 1 ? " isn't" : "s aren't"} linked yet.</b> Every action should deliver an approved priority, a Business Health improvement or a KPI. Nothing has been changed or deleted.
          </p>
          <Button size="sm" variant="outline" className="bg-white" onClick={() => setScope("needs_link")} data-testid="show-unlinked-btn">Review them</Button>
        </div>
      )}

      {view === "calendar" ? (
        <CalendarView actions={calActions} month={month} setMonth={setMonth} onOpen={setEditing} />
      ) : filtered.length === 0 ? (
        <EmptyState icon="ListChecks" title="No actions found" description="Try adjusting filters or create a new action."
          action={canEdit && <Button onClick={() => setCreating(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]">New action</Button>} />
      ) : view === "list" ? (
        <div className="space-y-2.5">
          {filtered.map((a) => (
            <ActionRow key={a.id} a={a} onClick={() => setEditing(a)} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {BOARD_COLS.map((col) => {
            const items = filtered.filter((a) => (col.match || [col.key]).includes(a.status));
            return (
              <div key={col.key} className="rounded-xl bg-[#E4E8EF]/50 p-3">
                <div className="mb-3 flex items-center justify-between px-1">
                  <span className="text-xs font-semibold text-[#1E2D44]/70">{col.label}</span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs text-[#1E2D44]/50">{items.length}</span>
                </div>
                <div className="space-y-2.5">
                  {items.map((a) => (
                    <div key={a.id} onClick={() => setEditing(a)} className="cursor-pointer rounded-lg border border-[#E4E8EF] bg-white p-3 transition-colors hover:border-[#FFC24D]">
                      <div className="flex items-center gap-1.5">
                        <RagBadge rag={LEVEL[a.priority_level]?.rag} dot={false} className="!px-1.5 !py-0 !text-[10px]">{LEVEL[a.priority_level]?.label}</RagBadge>
                        {a.is_overdue && <span className="text-[10px] font-semibold text-[#C95555]">Overdue</span>}
                      </div>
                      <div className="mt-1.5 text-sm font-medium text-[#0B1729] line-clamp-2">{a.title}</div>
                      <div className="mt-2 flex items-center justify-between text-[11px] text-[#1E2D44]/50">
                        <span>{a.owner_name}</span><span>{fmtDate(a.due_date)}</span>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <div className="py-6 text-center text-xs text-[#1E2D44]/35">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <ActionDialog action={editing} canEdit={canEdit} orgId={orgId} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creating && <CreateActionDialog orgId={orgId} members={members} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function CalendarView({ actions, month, setMonth, onOpen }) {
  const start = month.startOf("month");
  const gridStart = start.subtract((start.day() + 6) % 7, "day");
  const days = Array.from({ length: 42 }, (_, i) => gridStart.add(i, "day"));
  const byDay = {};
  actions.forEach((a) => {
    if (a.due_date) {
      const k = dayjs(a.due_date).format("YYYY-MM-DD");
      (byDay[k] = byDay[k] || []).push(a);
    }
  });
  const today = dayjs().format("YYYY-MM-DD");
  const dow = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div className="amden-card p-4" data-testid="actions-calendar">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-base font-bold text-[#0B1729]">{month.format("MMMM YYYY")}</h3>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setMonth((m) => m.subtract(1, "month"))} data-testid="cal-prev"><Icons.ChevronLeft size={16} /></Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => setMonth(dayjs().startOf("month"))} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setMonth((m) => m.add(1, "month"))} data-testid="cal-next"><Icons.ChevronRight size={16} /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {dow.map((d) => <div key={d} className="pb-1 text-center text-[11px] font-semibold uppercase tracking-wide text-[#1E2D44]/45">{d}</div>)}
        {days.map((d, i) => {
          const k = d.format("YYYY-MM-DD");
          const items = byDay[k] || [];
          const inMonth = d.month() === month.month();
          return (
            <div key={i} className={`min-h-[92px] rounded-lg border p-1.5 ${inMonth ? "border-[#EEF1F6] bg-white" : "border-transparent bg-[#F4F6F9]/40"} ${k === today ? "ring-1 ring-[#FFB000]" : ""}`}>
              <div className={`mb-1 text-right text-[11px] ${k === today ? "font-bold text-[#FFB000]" : inMonth ? "text-[#1E2D44]/55" : "text-[#1E2D44]/25"}`}>{d.date()}</div>
              <div className="space-y-1">
                {items.slice(0, 3).map((a) => (
                  <button key={a.id} onClick={() => onOpen(a)} className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[10px] transition-colors hover:bg-[#F4F6F9]" data-testid={`cal-action-${a.id}`}>
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${RAG[ACTION_STATUS[a.status]?.rag || "grey"].dot}`} />
                    <span className="truncate text-[#1E2D44]/80">{a.title}</span>
                  </button>
                ))}
                {items.length > 3 && <div className="px-1 text-[10px] text-[#1E2D44]/45">+{items.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActionRow({ a, onClick }) {  return (
    <div onClick={onClick} className="amden-card amden-card-hover flex cursor-pointer items-center gap-4 p-4" data-testid={`action-row-${a.id}`}>
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${RAG[ACTION_STATUS[a.status]?.rag || "grey"].dot}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-[#0B1729]">{a.title}</span>
          {a.is_overdue && <RagBadge rag="red" dot={false} className="!py-0 !text-[10px]">Overdue</RagBadge>}
          {a.needs_link_review && <RagBadge rag="amber" dot={false} className="!py-0 !text-[10px]" testId={`needs-link-badge-${a.id}`}>Needs linking</RagBadge>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[#1E2D44]/50">
          <span>{a.business_area}</span>
          {a.link_type ? <LinkChip a={a} /> : a.priority_title && <span className="flex items-center gap-1"><Icons.Target size={11} /> {a.priority_title}</span>}
          <span>{a.owner_name}</span>
        </div>
      </div>
      <div className="hidden w-28 sm:block"><ProgressBar value={a.percent_complete} color={RAG[ACTION_STATUS[a.status]?.rag || "grey"].solid} /></div>
      <RagBadge rag={LEVEL[a.priority_level]?.rag} dot={false} className="hidden md:inline-flex">{LEVEL[a.priority_level]?.label}</RagBadge>
      <div className={`w-24 text-right text-xs ${relDays(a.due_date) < 0 && a.status !== "completed" ? "font-semibold text-[#C95555]" : "text-[#1E2D44]/55"}`}>{fmtDate(a.due_date)}</div>
    </div>
  );
}

function ActionDialog({ action, canEdit, orgId, onClose, onSaved }) {
  const [pct, setPct] = useState(action.percent_complete);
  const [status, setStatus] = useState(action.status);
  const [blocker, setBlocker] = useState(action.blocker || "");
  const [overdueReason, setOverdueReason] = useState(action.overdue_reason || "");
  const [notes, setNotes] = useState(action.completion_notes || "");
  const [comment, setComment] = useState("");
  const [full, setFull] = useState(action);
  const [work, setWork] = useState(null);
  const [link, setLink] = useState({ link_type: "priority", link_id: "" });
  const [saving, setSaving] = useState(false);

  const loadWork = () => api.get(`/workspace/actions/${action.id}`).then((r) => setWork(r.data)).catch(() => setWork(null));
  useEffect(() => { api.get(`/actions/${action.id}`).then((r) => setFull(r.data)); loadWork(); }, [action.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsReason = action.is_overdue && status !== "completed";
  const closed = ["completed", "cancelled"].includes(action.status);
  const statusOptions = [
    ...STATUSES.filter((s) => !(action.requires_review && s === "completed" && action.status !== "completed")),
    ...(STATUSES.includes(action.status) ? [] : [action.status]),
  ];

  const linkNow = async () => {
    if (!link.link_id) return toast.error("Choose what this action delivers");
    try {
      await api.post(`/actions/${action.id}/link`, link);
      toast.success("Action linked");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (needsReason && !overdueReason) return toast.error("Please select a reason this action is overdue");
    setSaving(true);
    try {
      await api.patch(`/actions/${action.id}`, {
        percent_complete: pct, status, blocker: blocker || null,
        overdue_reason: overdueReason || null, completion_notes: notes || null,
      });
      if (comment.trim()) await api.post(`/actions/${action.id}/comments`, { body: comment.trim() });
      toast.success("Action updated");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="action-dialog">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <RagBadge rag={ACTION_STATUS[status]?.rag}>{ACTION_STATUS[status]?.label}</RagBadge>
            <RagBadge rag={LEVEL[action.priority_level]?.rag} dot={false}>{LEVEL[action.priority_level]?.label}</RagBadge>
            {work?.link_type && <LinkChip a={work} />}
          </div>
          <DialogTitle className="font-display text-xl">{action.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {action.description && <p className="text-sm text-[#1E2D44]/75">{action.description}</p>}
          {action.needs_link_review && canEdit && (
            <div className="rounded-xl border border-[#FFC24D]/70 bg-[#FFB000]/[0.06] p-4" data-testid="link-now-box">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[#0B1729]"><Icons.Link2 size={15} className="text-[#B87F1E]" /> This action isn't linked yet</div>
              <ActionLinkPicker orgId={orgId} value={link} onChange={setLink} label="Link it to" testId="link-now-picker" />
              <Button size="sm" onClick={linkNow} className="mt-3 bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="link-now-btn">Link now</Button>
            </div>
          )}
          {action.requires_review && (
            <p className="flex items-start gap-1.5 text-xs text-[#B87F1E]" data-testid="action-review-note">
              <Icons.ShieldCheck size={14} className="mt-px shrink-0" /> Needs independent review — the owner submits it from My Workspace and {work?.reviewer_name || "the reviewer"} approves it.
            </p>
          )}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="label-eyebrow mb-1">Owner</div>{action.owner_name || "—"}</div>
            <div><div className="label-eyebrow mb-1">Due date</div>{fmtDate(action.due_date)}</div>
            <div><div className="label-eyebrow mb-1">Business area</div>{action.business_area || "—"}</div>
            <div><div className="label-eyebrow mb-1">Priority</div>{action.priority_title || "—"}</div>
          </div>

          {canEdit && (
            <div className="rounded-xl border border-[#E4E8EF] p-4">
              <div className="space-y-4">
                <div>
                  <div className="mb-2 flex justify-between text-sm"><span className="text-[#1E2D44]/60">Completion</span><span className="font-semibold">{pct}%</span></div>
                  <Slider value={[pct]} onValueChange={(v) => setPct(v[0])} max={100} step={5} data-testid="action-progress-slider" />
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="mt-1" data-testid="action-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{statusOptions.map((s) => <SelectItem key={s} value={s}>{ACTION_STATUS[s]?.label || s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {(status === "blocked" || status === "waiting") && (
                  <div><Label className="text-xs">Blocker</Label><Input value={blocker} onChange={(e) => setBlocker(e.target.value)} className="mt-1" placeholder="What's blocking this?" data-testid="blocker-input" /></div>
                )}
                {needsReason && (
                  <div className="rounded-lg bg-[#C95555]/8 p-3">
                    <Label className="text-xs text-[#C95555]">This action is overdue — why?</Label>
                    <Select value={overdueReason} onValueChange={setOverdueReason}>
                      <SelectTrigger className="mt-1.5 bg-white" data-testid="overdue-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                      <SelectContent>{OVERDUE_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {status === "completed" && (
                  <div><Label className="text-xs">Completion notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" /></div>
                )}
              </div>
            </div>
          )}

          {/* Comments */}
          <div>
            <div className="label-eyebrow mb-2">Comments & activity</div>
            <div className="space-y-2.5">
              {(full.comments || []).map((c) => (
                <div key={c.id} className="rounded-lg bg-[#F4F6F9] p-3">
                  <div className="flex items-center justify-between"><span className="text-xs font-semibold text-[#0B1729]">{c.author_name}</span><span className="text-[10px] text-[#1E2D44]/40">{fmtDate(c.created_at)}</span></div>
                  <p className="mt-1 text-sm text-[#1E2D44]/75">{c.body}</p>
                </div>
              ))}
              {(full.comments || []).length === 0 && <p className="text-sm text-[#1E2D44]/40">No comments yet.</p>}
            </div>
            {canEdit && <Textarea value={comment} onChange={(e) => setComment(e.target.value)} className="mt-3" placeholder="Add a comment…" data-testid="action-comment-input" />}
          </div>
          {work && (
            <>
              <EvidencePanel a={work} canEdit={canEdit && ["owner", "manager"].includes(work.my_role) && !closed} onChanged={loadWork} />
              <HistoryTimeline events={work.history} />
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canEdit && <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-action-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Save"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateActionDialog({ orgId, members, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "", description: "", business_area: BUSINESS_AREAS[0], owner_id: "",
    due_date: "", priority_level: "normal", status: "not_started",
  });
  const [link, setLink] = useState({ link_type: "priority", link_id: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title) return toast.error("Add a title");
    if (!link.link_id) return toast.error("Choose the priority, improvement or KPI this action delivers");
    setSaving(true);
    try {
      const payload = { ...form, owner_id: form.owner_id || null, due_date: form.due_date || null, ...link };
      await api.post(`/actions?org_id=${orgId}`, payload);
      toast.success("Action created");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="create-action-dialog">
        <DialogHeader><DialogTitle>New action</DialogTitle></DialogHeader>
        <div className="space-y-3.5">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="new-action-title" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Owner</Label>
              <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                <SelectTrigger className="mt-1" data-testid="new-action-owner"><SelectValue placeholder="Assign" /></SelectTrigger>
                <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Priority level</Label>
              <Select value={form.priority_level} onValueChange={(v) => setForm({ ...form, priority_level: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{LEVEL[l].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Business area</Label>
              <Select value={form.business_area} onValueChange={(v) => setForm({ ...form, business_area: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{BUSINESS_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" /></div>
          </div>
          <ActionLinkPicker orgId={orgId} value={link} onChange={setLink} testId="new-action-link" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-new-action">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Create action"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
